import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { has_permission } from 'config/permissions';
import Loader from 'components/Loader';
import { showToast } from 'components/Toast';
import { DetailHeader } from 'components/Forms/DetailHeader';
import { TabAccordion } from 'components/Forms/TabAccordion';
import { wait, getUrlParams, dollarsToCents } from 'helpers/utils';
import { convertToFullDatetime } from 'helpers/date';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import {
  buildRecipientGroupsPayload,
  buildSuperOnlyPageFields,
  convertUrlToFile,
  getImageConfigForDuplication,
  getInitialValues,
  resetRestrictedFields,
  validateAllocationForm,
  validateEventForm,
} from './utils';
import { ModalComponent } from 'components/Modal';
import { getPagesState } from 'selectors/pages';
import { getRecipientsState } from 'selectors/recipients';
import { useAdminAuth } from 'hooks/useAdminAuth';
import { IPage, RecommendationPageProps } from 'store/pages/types';
import { getRecipients } from 'store/recipients/actions';
import {
  uploadFile,
  updateImageFilename,
  getAllPages,
  getPageDetail,
  getPages,
  updatePage,
  createPage,
} from 'store/pages/actions';
import { checksTab, pageTypes } from './constants';
import { CUSTOM_ALLOCATION_TYPES } from './Forms/DonationAllocation/DonationAllocationForm/constants';
import { useAccordionTab } from 'helpers/useAccordionTab';
import { ContentPreview } from './ContentPreview';
import { EventPageAccordion } from './Accordions/EventPageAccordion';
import { DonatePageAccordion } from './Accordions/DonatePageAccordion';
import { RecommendationPageAccordion } from './Accordions/RecommendationPageAccordion';
import { RecipientsRequestType } from 'store/recipients/types';
import { datadogLogs } from '@datadog/browser-logs';
import { getPageInteractionType, PageCategory, trackEvent } from 'helpers/mixpanelEvents';

const PagesDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const hasInitialized = useRef(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [tabStatus, setTabStatus] = useState<string[]>([]);
  const [initialFormValues, setInitialFormValues] = useState({});
  const [isPageDuplicated, setIsPageDuplicated] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCommunityFile, setSelectedCommunityFile] = useState<File | null>(null);
  const [selectedOptionalMediaFile, setSelectedOptionalMediaFile] = useState<File | null>(null);
  const [pageType, setPageType] = useState(getUrlParams()?.pageType || 'donate');
  const [editPageSelected, setEditPageSelected] = useState<IPage | undefined>(undefined);
  const [resetFieldNames, setResetFieldNames] = useState<string[]>([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);

  const recipientsState = useAppSelector(getRecipientsState);
  const pagesState = useAppSelector(getPagesState);

  const { clientId, activeRole, isSuper } = useAdminAuth();

  const filterRecipientsByProcessingRedirect = (
    recipientsSelected: { value: string; label: string }[] | undefined,
    processingRedirect: string
  ): string[] => {
    return (
      recipientsSelected
        ?.filter(item => {
          const recipient = recipientsState.data?.recipients.find(
            rec => rec.recipient_id === item.value
          );
          if (!recipient) return false;

          return processingRedirect === 'DE_OATH'
            ? recipient.de_status === 'ACTIVE'
            : recipient.stripe_status === 'ACTIVE';
        })
        ?.map(item => item.value) || []
    );
  };

  useEffect(() => {
    setTabStatus(new Array(pageTypes[pageType].length).fill(''));
  }, [pageType]);

  useEffect(() => {
    setTabStatus(prevStatus => ['in-progress', ...prevStatus.slice(1)]);
    // getAllPages for super-admins only; get client pages for partner admins
    if (isSuper && !pagesState.data.allPages) dispatch(getAllPages());
    // Skip when no client is resolved (null) or super's ALL (0); a page belongs to a specific client.
    if (clientId && !pagesState.data.clientPages)
      dispatch(getPages(clientId, pageType, activeRole));
    dispatch(
      getRecipients({ payment_processor: 'all', request_type: RecipientsRequestType.admin })
    );
  }, [clientId, activeRole]);

  const handleSubmitDonatePage = (values: any, clientId: number) => {
    setTabStatus(new Array(pageTypes[pageType].length).fill('success'));
    if (values.selectedFile) setSelectedFile(values.selectedFile);
    if (values.selectedCommunityFile) setSelectedCommunityFile(values.selectedCommunityFile);
    if (values.selectedOptionalMediaFile)
      setSelectedOptionalMediaFile(values.selectedOptionalMediaFile);
    const body = {
      value: values.page_name,
      query_str: values.query_string.toLowerCase(),
      desc: values.description,
      donation_amounts: values.donation_amounts.map(dollarsToCents),
      client_id: clientId,
      page_cta: values.page_cta,
      page_background: values.page_background,
      page_color_text: values.page_color_text,
      has_image: !!values.preview,
      impact_score_visibility: values.impact_score_visibility,
      show_more_visibility: values.show_more_visibility,
      is_enabled: true,
      page_type: values.page_type,
      recipient_ids: filterRecipientsByProcessingRedirect(
        values.recipients_selected,
        values.processing_redirect
      ),
      status: values.active_page_status ? 'ACTIVE' : 'PAUSED',
      custom_disclaimer: values.custom_disclaimer,
      whitelabeled: values.whitelabeled === 'TRUE',
      active_page_status: values.active_page_status,
      paused_misc: {
        paused_description: values.paused_description,
        paused_redirect_button: values.paused_redirect_button,
        paused_redirect_url: values.paused_redirect_url,
      },
      fundraising_goal: dollarsToCents(values.fundraising_goal),
      display_fundraising_bar: values.display_fundraising_bar === 'TRUE',
      learn_more_link: values.learn_more_link,
      bottom_url_text: values.bottom_url_text ?? '',
      page_category: values.page_category,
      has_community_image: !!values.communityPreview,
      processing_redirect: values.processing_redirect,
      is_public_searchable: values.is_public_searchable,
      recipient_order_by: values.recipient_order_by || null,
      is_oath_disclaimer_visible: values.is_oath_disclaimer_visible,
      recurring_enabled: values.recurring_enabled,
      recurring_interval: values.recurring_interval,
      optional_file_arrangement: values.optional_file_arrangement,
      has_optional_media: !!values.previewOptionalMedia,
      optional_media_type: values.selectedOptionalMediaFile
        ? values.selectedOptionalMediaFile.type.startsWith('video/')
          ? 'video'
          : 'image'
        : (editPageSelected?.[0]?.optional_media_type ?? null),
      ...buildSuperOnlyPageFields(values, isSuper),
      allocation_type: values.allocation_type,
      ...(CUSTOM_ALLOCATION_TYPES.includes(values.allocation_type) && {
        recipient_groups: buildRecipientGroupsPayload(
          values.recipient_groups,
          values.allocation_type
        ),
      }),
    };
    if (editPageSelected && !isPageDuplicated) {
      const updateBody = {
        client_id: clientId,
        page: {
          ...body,
          id: parseInt(getUrlParams().tagId || ''),
        },
      };
      dispatch(updatePage(updateBody, 'donate', activeRole));
    } else dispatch(createPage(body, 'donate', activeRole));
  };

  const handleSubmitEventPage = (values: any, clientId: number) => {
    setTabStatus(new Array(pageTypes[pageType].length).fill('success'));
    if (values.selectedFile) setSelectedFile(values.selectedFile);
    if (values.selectedCommunityFile) setSelectedCommunityFile(values.selectedCommunityFile);
    if (values.selectedOptionalMediaFile)
      setSelectedOptionalMediaFile(values.selectedOptionalMediaFile);
    const body = {
      value: values.page_name,
      subdescription: values.subdescription,
      start_datetime: convertToFullDatetime(
        `${moment(values.event_date).format('YYYY-MM-DD')} ${values.start_time}`
      ),
      end_datetime: values.end_time
        ? convertToFullDatetime(
            `${moment(values.event_date).format('YYYY-MM-DD')} ${values.end_time}`
          )
        : null,
      location_name: values.location_name,
      is_location_visible: values.is_location_visible,
      page_background: values.page_background,
      page_color_text: values.page_color_text,
      has_image: !!values.preview,
      desc: values.description,
      query_str: values.query_string.toLowerCase(),
      impact_score_visibility: values.impact_score_visibility,
      show_more_visibility: values.show_more_visibility,
      page_type: values.page_type,
      client_id: clientId,
      page_cta: values.page_cta,
      is_enabled: true,
      recipient_ids: filterRecipientsByProcessingRedirect(
        values.recipients_selected,
        values.processing_redirect
      ),
      tickets: (values.rows ?? []).map(({ id, ...row }, i) => ({
        ...row,
        name: row.name?.trim() ?? '',
        order: values.ticket_arrangement === 'MANUALLY' ? i : null,
        id: id?.length > 2 && !String(id).startsWith('copy-') ? id : null,
        price: dollarsToCents(row.price),
      })),
      location_desc: '',
      address_1: '',
      address_2: '',
      event_id: values.event_id,
      city: '',
      state: '',
      zipcode: '',
      country: '',
      doors_open_datetime: values.doors_open_before_event
        ? convertToFullDatetime(
            `${moment(values.event_date).format('YYYY-MM-DD')} ${values.doors_open_datetime}`
          )
        : null,
      post_donate_msg: '',
      email_msg: '',
      status: values.active_page_status ? 'ACTIVE' : 'PAUSED',
      custom_disclaimer: values.custom_disclaimer,
      whitelabeled: values.whitelabeled === 'TRUE',
      active_page_status: values.active_page_status,
      paused_misc: {
        paused_description: values.paused_description,
        paused_redirect_button: values.paused_redirect_button,
        paused_redirect_url: values.paused_redirect_url,
      },
      fundraising_goal: dollarsToCents(values.fundraising_goal),
      display_fundraising_bar: values.display_fundraising_bar === 'TRUE',
      learn_more_link: values.learn_more_link,
      bottom_url_text: values.bottom_url_text ?? '',
      page_category: values.page_category,
      has_community_image: !!values.communityPreview,
      processing_redirect: values.processing_redirect,
      is_public_searchable: values.is_public_searchable,
      recipient_order_by: values.recipient_order_by || null,
      is_oath_disclaimer_visible: values.is_oath_disclaimer_visible,
      optional_file_arrangement: values.optional_file_arrangement,
      has_optional_media: !!values.previewOptionalMedia,
      optional_media_type: values.selectedOptionalMediaFile
        ? values.selectedOptionalMediaFile.type.startsWith('video/')
          ? 'video'
          : 'image'
        : (editPageSelected?.[0]?.optional_media_type ?? null),
      ...buildSuperOnlyPageFields(values, isSuper),
      allocation_type: values.allocation_type,
      ...(CUSTOM_ALLOCATION_TYPES.includes(values.allocation_type) && {
        recipient_groups: buildRecipientGroupsPayload(
          values.recipient_groups,
          values.allocation_type
        ),
      }),
    };
    if (editPageSelected && !isPageDuplicated) {
      const updateBody = {
        ...editPageSelected[0],
        ...body,
        id: parseInt(getUrlParams().tagId || ''),
      };
      dispatch(updatePage(updateBody, 'event', activeRole));
    } else dispatch(createPage(body, 'event', activeRole));
  };

  const handleSubmitRecommendationPage = (values: any, clientId: number) => {
    setTabStatus(new Array(pageTypes[pageType].length).fill('success'));
    if (values.selectedFile) setSelectedFile(values.selectedFile);
    if (values.selectedCommunityFile) setSelectedCommunityFile(values.selectedCommunityFile);
    const body: RecommendationPageProps = {
      value: values.page_name,
      subdescription: values.subdescription,
      page_background: values.page_background,
      page_color_text: values.page_color_text,
      has_image: !!values.preview,
      recipient_details: values.recipient_details,
      recipient_order_by: 'custom',
      subtitle: values.subtitle,
      desc: values.description,
      query_str: values.query_string.toLowerCase(),
      client_id: clientId,
      is_enabled: true,
      recipient_ids: filterRecipientsByProcessingRedirect(
        values.recipients_selected,
        values.processing_redirect
      ),
      page_type: 'REC',
      donation_amounts: null,
      status: values.active_page_status ? 'ACTIVE' : 'PAUSED',
      custom_disclaimer: values.custom_disclaimer,
      whitelabeled: values.whitelabeled === 'TRUE',
      active_page_status: values.active_page_status,
      paused_misc: {
        paused_description: values.paused_description,
        paused_redirect_button: values.paused_redirect_button,
        paused_redirect_url: values.paused_redirect_url,
      },
      fundraising_goal: dollarsToCents(values.fundraising_goal),
      display_fundraising_bar: values.display_fundraising_bar === 'TRUE',
      learn_more_link: values.learn_more_link,
      bottom_url_text: values.bottom_url_text ?? '',
      page_category: values.page_category,
      has_community_image: !!values.communityPreview,
      processing_redirect: values.processing_redirect,
      is_public_searchable: values.is_public_searchable,
      is_oath_disclaimer_visible: values.is_oath_disclaimer_visible,
      optional_file_arrangement: values.optional_file_arrangement,
      has_optional_media: !!values.previewOptionalMedia,
      optional_media_type: values.selectedOptionalMediaFile
        ? values.selectedOptionalMediaFile.type.startsWith('video/')
          ? 'video'
          : 'image'
        : (editPageSelected?.[0]?.optional_media_type ?? null),
      ...buildSuperOnlyPageFields(values, isSuper),
    };
    if (editPageSelected && !isPageDuplicated) {
      const updateBody = {
        client_id: clientId,
        page: {
          ...body,
          id: parseInt(getUrlParams().tagId || ''),
        },
      };
      dispatch(updatePage(updateBody, 'recommendation', activeRole));
    } else dispatch(createPage(body, 'recommendation', activeRole));
  };

  const saveForm = async (values: any) => {
    if (!clientId) {
      showToast('A client must be selected to save a page', 'error');
      return;
    }

    if (pageType === 'donate') {
      handleSubmitDonatePage(values, clientId);
    } else if (pageType === 'event') {
      handleSubmitEventPage(values, clientId);
    } else if (pageType === 'recommendation') {
      handleSubmitRecommendationPage(values, clientId);
    }
  };

  useEffect(() => {
    if (pagesState.data?.clientPages?.pages && !hasInitialized.current) {
      const tagId = getUrlParams().tagId;
      if (tagId) {
        const selected = pagesState.data.clientPages.pages.filter(p => p.id === parseInt(tagId));
        if (selected?.length === 0) {
          navigate('/donate_pages');
          return;
        }

        if (selected?.length > 0) {
          hasInitialized.current = true;
          setPageLoaded(true);
          if (getUrlParams().copy !== null) setIsPageDuplicated(true);

          dispatch(
            getPageDetail(
              selected[0].query_str,
              selected[0].event_id ? 'event' : 'donate',
              activeRole
            )
          );
          setPageType(
            selected[0].event_id
              ? 'event'
              : selected[0].page_type === 'REC'
                ? 'recommendation'
                : 'donate'
          );
        }
      } else {
        hasInitialized.current = true;
        setPageLoaded(true);
        const urlPageType = getUrlParams().pageType;
        if (urlPageType) {
          setPageType(urlPageType);
        }
      }
    }
  }, [pagesState.data?.clientPages?.pages, navigate, dispatch]);

  useEffect(() => {
    if (pagesState.data?.pageDetail && pageLoaded && hasInitialized.current) {
      const selected = pagesState.data.clientPages.pages.filter(
        page => page.id === parseInt(getUrlParams().tagId || '')
      );

      if (selected?.length > 0) {
        const selectedPage = pagesState.data.pageDetail[selected[0].event_id ? 'events' : 'pages'];
        setEditPageSelected(selectedPage);

        const initialValues = getInitialValues(
          selectedPage,
          recipientsState,
          isPageDuplicated,
          pagesState.data.clientPages
        );

        if (isPageDuplicated) {
          const { values: patchedValues, resetFieldNames: resets } = resetRestrictedFields(
            initialValues,
            pagesState.data?.clientPages?.render_hints?.field_restrictions,
            pagesState.data?.clientPages?.preferred_payment_processor
          );
          setResetFieldNames(resets);

          const imageConfigs = getImageConfigForDuplication(selectedPage[0]);

          if (imageConfigs.length > 0) {
            (async () => {
              try {
                const results = await Promise.all(
                  imageConfigs.map(({ url, filename, fieldName }) =>
                    convertUrlToFile(url, filename).then(file => ({ fieldName, file }))
                  )
                );
                const fileFields = Object.fromEntries(
                  results
                    .filter(({ file }) => file !== null)
                    .map(({ fieldName, file }) => [fieldName, file])
                );
                setInitialFormValues({ ...patchedValues, ...fileFields });
              } catch (error) {
                datadogLogs.logger.error(
                  'Failed to convert image for duplication:',
                  {},
                  error as Error
                );
                setInitialFormValues(patchedValues);
              }
            })();
          } else {
            setInitialFormValues(patchedValues);
          }
        } else {
          setInitialFormValues(initialValues);
        }
      }
    }
  }, [
    pagesState.data?.pageDetail,
    pageLoaded,
    recipientsState,
    hasInitialized.current,
    isPageDuplicated,
  ]);

  useEffect(() => {
    if (!editPageSelected && pageLoaded && hasInitialized.current) {
      const initialValues = getInitialValues(
        undefined,
        undefined,
        isPageDuplicated,
        pagesState.data.clientPages
      );
      setInitialFormValues(initialValues);
    }
  }, [editPageSelected, pageLoaded, isPageDuplicated, hasInitialized.current]);

  const updateStatus = (item: number, status: string) => {
    setTabStatus(prevStatus =>
      prevStatus.map((s: string, index: number) => (index === item ? status : s))
    );
  };

  const validateForm = (values: any) => ({
    ...validateAllocationForm(values, pageType),
    ...(pageType === 'event' ? validateEventForm(values) : {}),
  });

  const { handleClickAccordion, tabIndexVisible } = useAccordionTab({
    updateStatus,
    tabStatus,
    pageType,
    checksTab,
  });

  useEffect(() => {
    setTabStatus(prevStatus => ['in-progress', ...prevStatus.slice(1)]);
  }, []);

  useEffect(() => {
    if (pagesState.isLoading || !pagesState.data?.pageCreated) return;

    const pageId =
      pagesState?.data?.pageCreated?.id ??
      pagesState?.data?.pageCreated?.page_id ??
      (editPageSelected ? editPageSelected[0].id : '');
    const entityId = String(pageId);
    const entityType: 'page' | 'event' = pageType === 'event' ? 'event' : 'page';

    trackEvent('Page Saved', {
      screen: 'page_detail',
      page_category: pageType as PageCategory,
      interaction_type: getPageInteractionType(Boolean(editPageSelected), isPageDuplicated),
      page_id: entityId,
    });

    setIsUploadingAssets(true);
    (async () => {
      let anyFailed = false;
      try {
        if (selectedFile) {
          const uuid = crypto.randomUUID();
          const filename = `tag_${uuid}.png`;
          const renamedFile = new File([selectedFile], filename, { type: 'image' });
          const uploaded = await (dispatch(uploadFile(renamedFile)) as unknown as Promise<boolean>);
          if (uploaded) {
            await updateImageFilename(entityType, entityId, 'principal_logo', filename, activeRole);
          } else {
            anyFailed = true;
          }
        }

        if (selectedCommunityFile) {
          const uuid = crypto.randomUUID();
          const filename = `tag_${uuid}.png`;
          const renamedCommunityFile = new File([selectedCommunityFile], `icon/${filename}`, {
            type: 'image',
          });
          const uploaded = await (dispatch(
            uploadFile(renamedCommunityFile)
          ) as unknown as Promise<boolean>);
          if (uploaded) {
            await updateImageFilename(entityType, entityId, 'community_logo', filename, activeRole);
          } else {
            anyFailed = true;
          }
        }

        if (selectedOptionalMediaFile) {
          const isVideo = selectedOptionalMediaFile.type.startsWith('video/');
          const ext = isVideo ? 'mp4' : 'png';
          const uuid = crypto.randomUUID();
          const filename = `tag_${uuid}.${ext}`;
          const renamedOptionalMediaFile = new File(
            [selectedOptionalMediaFile],
            `optional_media/${filename}`,
            { type: isVideo ? 'video' : 'image' }
          );
          const uploaded = await (dispatch(
            uploadFile(renamedOptionalMediaFile)
          ) as unknown as Promise<boolean>);
          if (uploaded) {
            await updateImageFilename(entityType, entityId, 'optional_media', filename, activeRole);
          } else {
            anyFailed = true;
          }
        }
      } catch {
        anyFailed = true;
      } finally {
        setIsUploadingAssets(false);
      }

      if (!anyFailed) {
        if (resetFieldNames.length > 0) {
          setShowResetModal(true);
        } else {
          showToast(
            `Success! Your page has been ${editPageSelected ? 'updated' : 'created'}`,
            'success'
          );
          wait(2000).then(() => navigate('/donate_pages'));
        }
      }
    })();
  }, [pagesState.data?.pageCreated]);

  const handleDismissResetModal = () => {
    setShowResetModal(false);
    showToast('Success! Your page has been created', 'success');
    wait(2000).then(() => navigate('/donate_pages'));
  };

  return (
    <>
      {(pagesState.isLoading || isUploadingAssets) && <Loader type="dots" />}
      <Form
        onSubmit={saveForm}
        initialValues={initialFormValues}
        validate={validateForm}
        render={({ handleSubmit, form }) => (
          <>
            {!has_permission('PAGES_DETAIL_ROUTE', 'R', activeRole) ? (
              <div className="content">
                <h1 className="title-permission">
                  Sorry, you don&apos;t have permission to see this screen.
                </h1>
              </div>
            ) : (
              <>
                <DetailHeader
                  saveForm={handleSubmit}
                  type="Page"
                  isNew={!editPageSelected || isPageDuplicated}
                  backTo={`/${pageType}_pages`}
                  setIsPreviewVisible={setIsPreviewVisible}
                  isPreviewVisible={isPreviewVisible}
                />
                <div className={classNames('content-full-width', { row: isPreviewVisible })}>
                  <div
                    className={classNames({
                      'show-preview': isPreviewVisible,
                    })}
                  >
                    <form onSubmit={handleSubmit}>
                      {pageTypes[pageType]
                        .map((type, index) => ({ type, index }))
                        .filter(
                          ({ type }) =>
                            isSuper || pageType === 'donate' || type !== 'Donation Options'
                        )
                        .map(({ type, index }, displayIndex) => (
                          <div key={index}>
                            <TabAccordion
                              index={index}
                              displayIndex={displayIndex}
                              itemVisible={tabIndexVisible}
                              type={type}
                              tabStatus={tabStatus}
                              handleClickAccordion={() => handleClickAccordion(index, form, 'tab')}
                            />
                            {pageType === 'donate' ? (
                              <DonatePageAccordion
                                index={index}
                                itemVisible={tabIndexVisible}
                                formState={form}
                                handleClickAccordion={index =>
                                  handleClickAccordion(index, form, 'next')
                                }
                                isPreviewVisible={isPreviewVisible}
                                editPageSelected={editPageSelected}
                                isPageDuplicated={isPageDuplicated}
                              />
                            ) : pageType === 'event' ? (
                              <EventPageAccordion
                                index={index}
                                itemVisible={tabIndexVisible}
                                formState={form}
                                handleClickAccordion={index =>
                                  handleClickAccordion(index, form, 'next')
                                }
                                isPreviewVisible={isPreviewVisible}
                                editPageSelected={editPageSelected}
                                isPageDuplicated={isPageDuplicated}
                              />
                            ) : (
                              <RecommendationPageAccordion
                                index={index}
                                itemVisible={tabIndexVisible}
                                formState={form}
                                handleClickAccordion={index =>
                                  handleClickAccordion(index, form, 'next')
                                }
                                isPreviewVisible={isPreviewVisible}
                                editPageSelected={editPageSelected}
                                isPageDuplicated={isPageDuplicated}
                              />
                            )}
                          </div>
                        ))}
                    </form>
                  </div>
                  <ContentPreview isPreviewVisible={isPreviewVisible} pageType={pageType} />
                </div>
              </>
            )}
          </>
        )}
      />
      <ModalComponent isOpen={showResetModal} closeModal={handleDismissResetModal}>
        <span className="oath-eyebrow inkBlue" style={{ marginBottom: '10px' }}>
          Heads up
        </span>
        <span className="oath-body inkBlue">
          The following settings were reset to their default values because they cannot be changed
          for this account:{' '}
          {resetFieldNames.map((name, index) => (
            <span key={name}>
              <strong>{name.replace(/ /g, '\u00a0')}</strong>
              {index < resetFieldNames.length - 1 ? ', ' : '.'}
            </span>
          ))}
        </span>
        <span className="oath-body inkBlue" style={{ marginTop: '10px' }}>
          This does not affect the original page.
        </span>
        <button
          type="button"
          className="btn-inkBlue btn btn-secondary"
          onClick={handleDismissResetModal}
        >
          Got it
        </button>
      </ModalComponent>
    </>
  );
};

export default PagesDetail;
